import React, { useState } from 'react';

import TpDialog from '../shared/tp_dialog';
import { discard_guard_text } from '../shared/dialog_logic';
import { DialogFooter, ErrorBox, FieldError, NoteField } from './dialog_parts';
import { KIND } from './error_handlers';
import { day_text, pass_day, rupees } from './adapters';
import { pass_form_errors } from './form_errors';
import { DIRECTIVE_ACTION, DIRECTIVE_CHOICE, unchanged_since } from './params';

/*
 * PROD-7828 R6 — the free-pass directive dialogs (D83/D88/D92). Ops never write the ledger: they change
 * where the engine may place passes, and the recompute places them.
 *   REMOVE: "move it to another day" (names the target, N8) or "keep the pass unused this month" (N7);
 *   APPLY: pin a day; if that takes an automatic pass from another day the server asks first (N6, E7);
 *   RESET_AUTO: drop every pin and skip (N9).
 * state is a free_pass_view; a DIRECTIVE_CHANGED refusal hands the current one back through the parent.
 */
const TITLES = {
  [DIRECTIVE_ACTION.REMOVE]: 'Remove the free pass',
  [DIRECTIVE_ACTION.APPLY]: 'Use a free pass',
  [DIRECTIVE_ACTION.RESET_AUTO]: 'Reset free passes to automatic'
};

const SUBMITS = {
  [DIRECTIVE_ACTION.REMOVE]: 'Remove free pass',
  [DIRECTIVE_ACTION.APPLY]: 'Use free pass',
  [DIRECTIVE_ACTION.RESET_AUTO]: 'Reset to automatic'
};

const PassDialog = ({
  action,
  state,
  day,
  teacher_name,
  saving,
  error,
  on_submit,
  on_close,
  on_focus_lost
}) => {
  const [form, setForm] = useState({ choice: '', move_to: '', note: '' });
  const [touched, setTouched] = useState(false);
  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const errors = pass_form_errors(action, form);
  const server = (error && error.fields) || {};
  const on_day = day ? pass_day(state, day) : null;
  const targets = state.move_targets.filter((d) => d.day_ist !== day);

  const body = { action, day, ...form };
  const [sent, setSent] = useState(null);
  const send = () => {
    setTouched(true);
    if (Object.values(errors).some(Boolean)) return;
    setSent(body);
    on_submit(body, false);
  };
  // CD-213: the confirm answers only the body the server refused; after an edit it is a plain send again.
  const confirmable = !!error && error.kind === KIND.CONFIRM && unchanged_since(sent, body);
  const shown = error && error.kind === KIND.CONFIRM && !confirmable ? null : error;

  return (
    <TpDialog
      title_id="tp-pe-pass-title"
      busy={saving}
      guard_text={discard_guard_text(form.note)}
      on_close={on_close}
      on_focus_lost={on_focus_lost}
    >
      <div className="tp-modal">
        <h4 id="tp-pe-pass-title">{TITLES[action]}</h4>
        {/* FP-2 (C5): placement is automatic by default; this manual override is for a teacher ask. */}
        <div className="tp-hint">Use only when the teacher asks for it.</div>
        <div className="tp-ctx">
          <b>{teacher_name}</b> · {state.month_text} · {state.balance_text}
          {day ? (
            <div>
              {day_text(day)}
              {on_day ? ` · eligible penalties ${on_day.value_text}` : ''}
            </div>
          ) : null}
        </div>

        {action === DIRECTIVE_ACTION.REMOVE ? (
          <fieldset className="tp-field">
            <legend className="tp-card-field-label">What happens to the pass?</legend>
            <label className="tp-pe-choice">
              <input
                type="radio"
                name="tp-pe-pass-choice"
                checked={form.choice === DIRECTIVE_CHOICE.MOVE}
                onChange={() => set('choice', DIRECTIVE_CHOICE.MOVE)}
              />{' '}
              Move it to another day
            </label>
            {form.choice === DIRECTIVE_CHOICE.MOVE ? (
              <div className="tp-field">
                <label htmlFor="tp-pe-pass-move">Move to</label>
                <select
                  id="tp-pe-pass-move"
                  className="tp-select"
                  value={form.move_to}
                  onChange={(e) => set('move_to', e.target.value)}
                >
                  <option value="">Choose a day</option>
                  {targets.map((d) => (
                    <option key={d.day_ist} value={d.day_ist}>
                      {d.day_text} · {d.value_text}
                    </option>
                  ))}
                </select>
                {targets.length ? null : (
                  <span className="tp-hint">No other day this month has an uncovered penalty.</span>
                )}
                <FieldError text={(touched && errors.move_to) || server.move_to} />
              </div>
            ) : null}
            <label className="tp-pe-choice">
              <input
                type="radio"
                name="tp-pe-pass-choice"
                checked={form.choice === DIRECTIVE_CHOICE.KEEP_UNUSED}
                onChange={() => set('choice', DIRECTIVE_CHOICE.KEEP_UNUSED)}
              />{' '}
              Keep the pass unused this month
            </label>
            {form.choice === DIRECTIVE_CHOICE.KEEP_UNUSED ? (
              <div className="tp-hint">
                No automatic free pass for the rest of {state.month_text}. Passes already on other
                days stay there, and the unused pass carries to the next month.
              </div>
            ) : null}
            <FieldError text={touched && errors.choice} />
          </fieldset>
        ) : null}

        {action === DIRECTIVE_ACTION.APPLY ? (
          <div className="tp-hint">
            One pass covers every eligible penalty on {day_text(day)}, Ops-added ones included.
          </div>
        ) : null}

        {action === DIRECTIVE_ACTION.RESET_AUTO ? (
          <div className="tp-hint">
            Every day Ops chose or skipped is cleared, and the system places the passes on the
            highest-value days again.
          </div>
        ) : null}

        {confirmable && error.displaced.length ? (
          <ul className="tp-dup-list">
            {error.displaced.map((d) => (
              <li key={d.day_ist}>
                {day_text(d.day_ist)} loses its automatic pass and is charged {rupees(d.value)}
              </li>
            ))}
          </ul>
        ) : null}

        <NoteField
          id="tp-pe-pass-note"
          label="Note (internal, required)"
          value={form.note}
          on_change={(v) => set('note', v)}
          error_text={(touched && errors.note) || server.note}
        />
        <ErrorBox error={shown} />
        <DialogFooter
          saving={saving}
          error={shown}
          submit_label={SUBMITS[action]}
          confirm_label="Use it anyway"
          on_submit={send}
          on_confirm={() => on_submit(sent, true)}
          on_close={on_close}
        />
      </div>
    </TpDialog>
  );
};

export default PassDialog;
